import { isPermissionsEnabled } from "./utils";

export const ADMIN_AUTHORITY = "ROLE_admin";

/**
 * The realm-seeded staff claim that says an account is staff, independent of any role it holds.
 *
 * Flat and boolean, and both of those are deliberate. An underscore rather than a dot, because a dot
 * would have nested it under a `parkit` object and `tokenParsed.parkit_staff` would read undefined.
 * A boolean rather than a string, because `"false" === true` is false but `Boolean("false")` is true,
 * and only one of those two mistakes fails in the safe direction.
 */
export const STAFF_CLAIM = "parkit_staff";

/**
 * Super Admin. Gates staff administration and the audit view.
 *
 * Deliberately not ADMIN_AUTHORITY: every staff account still holds the legacy `admin` role until
 * legacy-admin removal completes, so gating on that would hand staff administration — including privilege granting —
 * to every employee.
 */
export const SUPER_ADMIN_AUTHORITY = "ROLE_ADM";

/**
 * Realm roles as `ROLE_`-prefixed authorities.
 *
 * A note rather than a fix, because the prefixing is what the legacy `ROLE_admin` checks depend on.
 * Permission roles are realm roles too, so `perm:payout:release` arrives here as
 * `ROLE_perm:payout:release`. Nothing is broken by that today: gating reads the permission set from
 * `GET /users/me/permissions`, never the token's roles. But anyone who later reaches for the token as
 * a shortcut gets a silent mismatch rather than an error, so reach for `usePermissions().can` instead.
 */
export const getKeycloakAuthorities = (keycloak) => {
  const token = keycloak?.tokenParsed ?? {};
  const roles = token.realm_access?.roles ?? [];

  return new Set(
    roles.map((role) => (role.startsWith("ROLE_") ? role : `ROLE_${role}`))
  );
};

export const hasKeycloakAuthority = (keycloak, authority) =>
  getKeycloakAuthorities(keycloak).has(authority);

/**
 * Whether the token carries the realm-seeded staff claim.
 *
 * Absent means not staff. The mapper omits the claim entirely for a user without the attribute, so
 * `undefined` is the normal reading for a customer and must be false — this is the one comparison
 * here that would fail *open* if it were written as a loose truthiness test against the wrong thing.
 */
export const hasStaffClaim = (keycloak) => (keycloak?.tokenParsed ?? {})[STAFF_CLAIM] === true;

/**
 * Whether this token belongs to a member of staff at all, which every route requires.
 *
 * The portal authenticates against `parkit-user`, the same public client the customer web app uses,
 * so a customer who opens the portal URL reaches an ordinary login page and signs in successfully.
 * Something has to reject them, and this is it.
 *
 * **Which signal it reads is the largest consequence of the permissions flag.** The legacy `admin`
 * role is a perfect discriminator only for as long as every staff account holds it and no customer
 * does. Legacy-admin removal drops it from staff accounts, at which point a gate written against it stops
 * rejecting *anyone*: the portal opens to every authenticated customer with no error, no failing test
 * and no symptom anybody would notice. So with the flag on this reads the `parkit_staff` claim
 * instead, which describes what it is actually asking.
 *
 * The two directions of failure are not symmetric, which is why the switch sits behind the flag at
 * all. A misconfigured claim locks staff out — loud, immediately reported, and reverted by turning
 * the flag off. Reading a role that no longer discriminates lets everyone in silently. Keeping the
 * old reading available means the loud failure has a 30-second remedy.
 *
 * **Legacy-admin removal requires this flag permanently on**, and removing `admin` from any staff account is
 * blocked on that, verified against a real staff token and a real customer token rather than against
 * a fixture written from the same assumption as this code.
 */
export const hasStaffSignal = (keycloak) =>
  isPermissionsEnabled()
    ? hasStaffClaim(keycloak)
    : hasKeycloakAuthority(keycloak, ADMIN_AUTHORITY);
